import React, { useEffect, useRef, useState } from 'react';
import { getShellActions, loadApp } from '@nab/x-runtime';
import { Modal } from '@nab/nui-react';
import constants from '../../util/constants';
import { AppLoader } from '@nab/nab-x-sdk-browser';
import { debug, error } from '../../util/logger';
import { StyledModalContent } from './IdentityApp.style';

const IdentityApp: React.FunctionComponent<{ appConfig, appName, rootElement }> = ({appConfig, appName, rootElement}) => {
    const appActions = getShellActions();
    const identityRootReference = useRef(null);
    const modelReference = useRef(null);
    const [showModal, setShowModal] = useState(false);
    const [identityAppTag, setIdentityAppTag] = useState(appConfig.identityAppConfig.identityMiniAppTag);
    const [identityAppConfig, setIdentityAppConfig] = useState(appConfig.identityAppConfig.appConfig);

    const alreadyLoggedIn = () => {
        return !!sessionStorage.getItem(constants.BEARER_TOKEN);
    };

    const handleLoginRequest = (event) => {
        debug(appConfig, 'login request event received');
        debug(appConfig, 'already Logged In ' + alreadyLoggedIn());
        setIdentityAppTag(appConfig.identityAppConfig.identityMiniAppTag);
        setIdentityAppConfig(event?.identityAppConfig || appConfig.identityAppConfig.appConfig);

        if (event && typeof event.identityAppTag !== 'undefined' && event.identityAppTag) {
            debug(appConfig, 'Tag received ' + event.identityAppTag);
            setIdentityAppTag(event.identityAppTag);
        }
        setShowModal(true);

    };

    const handleUserAuthenticatedEvent = () => {
        debug(appConfig, 'user authenticated event received');
        if (AppLoader.getConfig(appName) && AppLoader.getConfig(appName).properties
            && !AppLoader.getConfig(appName).properties.skipAuthOnRender) {
            AppLoader.requestToken(appName, rootElement);
        }
        setShowModal(false);
    };

    const handleUserCancelEvent = () => {
        debug(appConfig, 'user cancel event received');
        setShowModal(false);

    };

    const handleESCKeyEvent = (event) => {
        if (event.keyCode === 27) {
            debug(appConfig, 'escape key event received');
            setShowModal(false);
        }
    };

    useEffect(() => {
        debug(appConfig, 'subscribe to events');

        //Listening for login request
        appActions.addEventListener(constants.EVENT_LOGIN, handleLoginRequest);

        if (appConfig.identityAppConfig.appType == constants.APP_TYPE_VERSION_2) {
            //Listening for user authenticated event
            appActions.addEventListener(constants.EVENT_USER_AUTHENTICATED_XV2, handleUserAuthenticatedEvent);

            //Listening for user cancel event
            appActions.addEventListener(constants.EVENT_USER_CANCELLED_XV2, handleUserCancelEvent);
        } else {
            //Listening for user authenticated event
            appActions.addEventListener(constants.EVENT_USER_AUTHENTICATED, handleUserAuthenticatedEvent);

            //Listening for user cancel event
            appActions.addEventListener(constants.EVENT_USER_CANCELLED, handleUserCancelEvent);
        }

        //Listening for ESC Key event
        document.addEventListener(constants.KEY_DOWN, handleESCKeyEvent, false);
        return () => {
            debug(appConfig, 'unsubscribe to events');
            //Unsubscribe for login request
            appActions.removeEventListener(constants.EVENT_LOGIN, handleLoginRequest);

            if (appConfig.identityAppConfig.appType == constants.APP_TYPE_VERSION_2) {

                //Unsubscribe for user authenticated event
                appActions.removeEventListener(constants.EVENT_USER_AUTHENTICATED_XV2, handleUserAuthenticatedEvent);

                //Unsubscribe for user cancel event
                appActions.removeEventListener(constants.EVENT_USER_CANCELLED_XV2, handleUserCancelEvent);
            } else {
                //Unsubscribe for user authenticated event
                appActions.removeEventListener(constants.EVENT_USER_AUTHENTICATED, handleUserAuthenticatedEvent);

                //Unsubscribe for user cancel event
                appActions.removeEventListener(constants.EVENT_USER_CANCELLED, handleUserCancelEvent);
            }

            //Unsubscribe for ESC Key event
            document.removeEventListener(constants.KEY_DOWN, handleESCKeyEvent, false);
        };
    }, []);

    useEffect(() => {
        let loaderPromise;

        if (showModal) {

            let appLoaderConfig;

            if (appConfig.identityAppConfig.appType == constants.APP_TYPE_VERSION_2) {
                appLoaderConfig = {
                    appMetaData: {
                        id: identityAppTag,
                        appType: appConfig.identityAppConfig.appType,
                        tag: identityAppTag,
                        assetManifestUrl: `${appConfig.identityAppConfig.identityMiniAppUrl}/asset-manifest.json`,
                        entryPoint: identityAppTag + '.js',
                    },
                    params: appConfig.identityAppConfig.params,
                    appConfig: { ...appConfig.identityAppConfig.appConfig, ...identityAppConfig },
                    appData: appConfig.identityAppConfig.appData,
                    hostElement: identityRootReference.current
                };
            } else {

                appLoaderConfig = {
                    appMetaData: {
                        id: identityAppTag,
                        appType: appConfig.identityAppConfig.appType,
                        tag: identityAppTag,
                        assetManifestUrl: `${appConfig.identityAppConfig.identityMiniAppUrl}/asset-manifest.json`,
                        properties: {appConfig: appConfig.identityAppConfig.appConfig}
                    },
                    hostElement: identityRootReference.current
                };
            }

            const load = async () => {
                try {
                    debug(appConfig, `Before loaderPromise`, identityRootReference.current);
                    loaderPromise = loadApp(appLoaderConfig);
                    debug(appConfig, `Before Await`, identityRootReference.current);
                    await loaderPromise;
                    debug(appConfig, `App ${identityAppTag} loaded successfully`);

                    await appActions.dispatchEvent(constants.APP_SHELL_LOADED_EVENT, {
                        appLoaded: true,
                        appName: identityAppTag
                    });
                } catch (e) {

                    setShowModal(false);
                    error(appConfig, `Failed to load app ${identityAppTag}: \n${(e && e.message) || e}`);
                    await appActions.dispatchEvent(constants.APP_SHELL_LOADED_EVENT, {
                        appLoaded: false,
                        appName: identityAppTag,
                        errorMessage: e.message
                    });

                }
            };
            load();
        }
        return () => {
            if (loaderPromise) {
                loaderPromise.then((app)=>{
                    app.unload();
                })
            }
        };
    }, [identityRootReference, showModal]);

    const shadowRoot = document.querySelector('nab-idp-password')?.shadowRoot;

    return (
        <div ref={modelReference}>
            {showModal && <Modal rootNode={shadowRoot}>
                <StyledModalContent>
                    <div ref={identityRootReference} />
                </StyledModalContent>
            </Modal>}
        </div>
    );
};

export default IdentityApp;

